import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Loader2, Mic, Newspaper, Search, Shield, Trophy, UserRound, Users } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ClubCrest } from "@/components/fsl/ClubCrest";
import { api } from "@/lib/api";

const META = {
  tournament: ["Tornei", Trophy],
  competition: ["Serie e gironi", BarChart3],
  club: ["Società", Shield],
  team: ["Squadre", Users],
  player: ["Giocatori", UserRound],
  interview: ["Interviste", Mic],
  news: ["News", Newspaper],
};
const ORDER = ["tournament", "competition", "club", "team", "player", "interview", "news"];

export function GlobalSearch({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);
  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setItems(null); return; }
    timer.current = setTimeout(() => {
      setBusy(true);
      api.get("/public/search", { params: { q: q.trim() } }).then((r) => setItems(r.data.items)).catch(() => setItems([])).finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, open]);
  const go = (it) => { setOpen(false); setQ(""); setItems(null); navigate(it.to); };
  const groups = ORDER.map((k) => [k, (items || []).filter((i) => i.type === k)]).filter(([, l]) => l.length);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`h-11 w-11 rounded-md border border-white/20 inline-flex items-center justify-center hover:border-fsl-gold/60 transition-colors ${className}`} aria-label="Cerca" title="Cerca giocatori, tornei, squadre, interviste" data-testid="global-search-button"><Search className="h-5 w-5" /></button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setQ(""); setItems(null); } }}>
        <DialogContent className="bg-navy-800 border-white/20 text-fsl-white rounded-xl p-0 max-w-xl top-[12%] translate-y-0 overflow-hidden" aria-describedby={undefined} data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Cerca</DialogTitle>
          <div className="flex items-center gap-3 px-4 h-14 border-b border-white/10">
            {busy ? <Loader2 className="h-5 w-5 text-fsl-gold animate-spin shrink-0" /> : <Search className="h-5 w-5 text-fsl-gold shrink-0" />}
            <input autoFocus className="flex-1 bg-transparent outline-none text-base placeholder:text-fsl-slate pr-6" placeholder="Cerca un giocatore, un torneo, una serie, una squadra, un'intervista…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && items?.length) go(items[0]); }} data-testid="global-search-input" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {q.trim().length < 2 ? <p className="px-4 py-6 text-sm text-fsl-slate" data-testid="global-search-hint">Scrivi almeno 2 lettere: cerchiamo tra tornei, serie, società, squadre, giocatori con profilo pubblico, interviste e news.</p>
              : items && items.length === 0 && !busy ? <p className="px-4 py-6 text-sm text-fsl-slate" data-testid="global-search-empty">Nessun risultato per «{q}».</p>
              : groups.map(([k, list]) => {
                const [label, Icon] = META[k];
                return (
                  <div key={k} className="py-2" data-testid={`global-search-group-${k}`}>
                    <div className="px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-fsl-gold inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</div>
                    {list.map((it, i) => (
                      <button key={`${k}-${i}`} type="button" onClick={() => go(it)} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/5 transition-colors" data-testid={`global-search-result-${k}-${i}`}>
                        {k === "club" ? <ClubCrest club={{ crest_url: it.crest_url, colors: it.colors, name: it.label }} size={28} /> : k === "player" && it.photo_url ? <img src={it.photo_url} alt="" className="h-7 w-7 rounded-full object-cover" /> : <span className="h-7 w-7 rounded-full bg-white/5 inline-flex items-center justify-center text-fsl-slate"><Icon className="h-3.5 w-3.5" /></span>}
                        <span className="min-w-0"><span className="block font-semibold text-sm truncate">{it.label}</span><span className="block text-xs text-fsl-slate truncate">{it.sub}</span></span>
                      </button>
                    ))}
                  </div>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
